import React, { useEffect, useState } from 'react';
import axios from 'axios';
import cn from 'classnames';
import { useForm } from 'react-hook-form';
import * as PropTypes from 'prop-types';
/**/
import { selector_auth } from '../../reducer/auth';
import { useSelector } from 'react-redux';
import endpoints from '../../define/endpoints';
import { ModalResponse } from '../modals';
import { useUUID } from '../../hooks/useUUID';
import { Input } from '../Input';
import { RadioList } from '../modals/RadioList';
import InputPhone from '../InputPhone';
import Typography, {
  TypographyStates,
  TypographyVariants,
  TypographyWeights,
} from '../../ui-components/Typography/Typography';
import { TRANSLATIONS } from '../../../translations';
import Button, {
  ButtonSizes,
  ButtonStyles,
  ButtonWidths,
} from '../Button/Button';

const _FIRST_NAME = 'name';
const _EMAIL = 'email';
const _PHONE = 'phone';
const _NOTES = 'notes';
const _PRIVACY_AND_TERMS = 'privacy_and_condition_terms';

const B2BForm = ({
  hubspotFormId,
  tag = "PER LE AZIENDE",
  heading = "In cerca di un regalo per il team? Mettiamoci in contatto!",
  emailLabel = "E-mail aziendale*",
  notesLabel = "Dicci di più su cosa hai in mente*",
  notesHelper = "Scrivere qui eventuali dettagli aggiuntivi relativi all'ordine",
  notesHelperBelow = "false",
  submitLabel = app_main_wpml.send_request,
  submitSize = "s",
  textAreaRows = 7,
}) => {
  const helperBelow = notesHelperBelow === "true" || notesHelperBelow === true;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const checkboxID = useUUID();
  const { register, handleSubmit, errors, setValue } = useForm({
    reValidateMode: 'onChange',
  });

  const handlePrivacyClick = () => {
    document.getElementById('iubenda-privacy-trigger').click();
  };

  return (
    <>
      <ModalResponse
        shouldScroll={false}
        isOpen={isModalOpen}
        onClickOverlay={() => setIsModalOpen(false)}
        onClickButton={() => setIsModalOpen(false)}
        isSuccess={isSuccess}
        successTitle={'Richiesta effettuata'}
        failTitle={TRANSLATIONS.UNEXPECTED_ERROR}
        successText={
          <Typography variant={TypographyVariants.p2}>
            La tua richiesta è stata inviata,
            <br />
            un nostro operatore ti contatterà presto.
          </Typography>
        }
        failText={
          <Typography variant={TypographyVariants.p2}>
            Controlla i dati inseriti e invia la richiesta.
          </Typography>
        }
      >
        <div style={{ minWidth: '170px' }}>
          <Button
            size={ButtonSizes.s}
            width={ButtonWidths.isFull}
            style={ButtonStyles.empty}
            label={isSuccess ? TRANSLATIONS.CLOSE : TRANSLATIONS.RETRY_NOW}
            onClick={() => setIsModalOpen(false)}
          />
        </div>
      </ModalResponse>

      {tag ? (
        <div className="tag light blue">
          {tag}
        </div>
      ) : null}

      {heading ? (
        <div className="mt-m mb-m">
          <Typography
            variant={TypographyVariants.h6}
            weight={TypographyWeights['semi-bold']}
          >
            {heading}
          </Typography>
        </div>
      ) : null}
      <form
        data-gtm-item="b2bForm"
        data-gtm-detail="contact-form-page"
        onSubmit={handleSubmit(async (data) => {
          try {
            await axios({
              method: 'post',
              url: `${endpoints.core}/form-requests`,
              data: {
                from: {
                  first_name: data[_FIRST_NAME],
                  //last_name: data[_LAST_NAME],
                  email: data[_EMAIL],
                },
                order: {
                  phone: data[_PHONE],
                  notes: data[_NOTES],
                },
                hubspot_form_id: hubspotFormId,
                privacy: data[_PRIVACY_AND_TERMS],
              },
            });

            setIsSuccess(true);
            // If the axios call is successful, push the event to dataLayer
            dataLayer.push({ event: 'bbFormSubmitted' });
          } catch (error) {
            setIsSuccess(false);
            // console.error('Error submitting form:', error);
          } finally {
            setIsModalOpen(true);
            const values = [];
            values.push({ [_NOTES]: '' });
            setValue(values);
          }
        })}
      >
        <div className="d-flex flex-column gap-3xs" style={{ gap: '20px' }}>
          <Input
            errored={!!errors[_FIRST_NAME]}
            shadowed
            label={app_main_wpml.contact_name + '*'}
            name={_FIRST_NAME}
            setRef={register({ required: true, max: 1000 })}
            data-gtm-item="b2b-form"
            data-gtm-detail="name"
          />
          <Input
            errored={!!errors[_EMAIL]}
            shadowed
            label={emailLabel}
            name={_EMAIL}
            setRef={register({
              required: true,
              max: 1000,
              pattern: /^\S+@\S+$/i,
            })}
            data-gtm-item="b2b-form"
            data-gtm-detail="email"
          />
          <Input
            errored={!!errors[_PHONE]}
            shadowed
            label={app_main_wpml.phone}
            name={_PHONE}
            setRef={register({
              required: false,
              pattern: /^\d+$/,
            })}
            data-gtm-item="b2b-form"
            data-gtm-detail="phone"
          />
          <Input
            errored={!!errors[_NOTES]}
            resize={false}
            asTextArea
            shadowed
            rows={Number(textAreaRows)}
            label={notesLabel}
            name={_NOTES}
            setRef={register({ required: true, max: 1000 })}
            data-gtm-item="b2b-form"
            data-gtm-detail="message"
          />
        </div>

        {!helperBelow && (
          <div className="mt-3xs mb-m">
            <Typography
              variant={TypographyVariants.p3}
              state={TypographyStates['neutral-light']}
            >
              {notesHelper}
            </Typography>
          </div>
        )}

        <div className={helperBelow ? "mt-s" : ""}>
          <div
            className={cn('fake-checkbox', {
              'fake-checkbox--errored': !!errors[_PRIVACY_AND_TERMS],
            })}
          >
            <input
              className="fake-checkbox__input"
              type="checkbox"
              name={_PRIVACY_AND_TERMS}
              ref={register({ required: true })}
              id={checkboxID}
              data-gtm-item="contact-form"
              data-gtm-detail="privacy"
            />
            <label htmlFor={checkboxID} className="fake-checkbox__label">
              <span
                className="iconify inline"
                data-icon="mdi:check-bold"
              ></span>
            </label>
            <p className="fake-checkbox__text">
              {app_main_wpml.read_and_agree}{' '}
              <span
                className="fake-checkbox__link"
                style={{ textDecoration: 'underline' }}
                onClick={() =>
                  window.open(
                    'https://freedome.it/termini-e-condizioni-clienti/'
                  )
                }
              >
                Termini e Condizioni Cliente
              </span>{' '}
              {app_main_wpml.and_the}{' '}
              <span
                className="fake-checkbox__link"
                style={{ textDecoration: 'underline' }}
                onClick={handlePrivacyClick}
              >
                {app_main_wpml.privacy_cta}
              </span>
            </p>
          </div>
        </div>
        <button
          className={`beta-btn beta-btn--primary beta-btn--${submitSize} beta-btn--full ${helperBelow ? "mt-s" : "mt-xl"}`}
          type="submit"
        >
          {submitLabel}
        </button>
        {helperBelow && (
          <div className="mt-s">
            <Typography
              variant={TypographyVariants.p3}
              state={TypographyStates['neutral-light']}
            >
              {notesHelper}
            </Typography>
          </div>
        )}
      </form>
    </>
  );
};

B2BForm.propTypes = {
  hubspotFormId: PropTypes.string,
  tag: PropTypes.string,
  heading: PropTypes.string,
  emailLabel: PropTypes.string,
  notesLabel: PropTypes.string,
  notesHelper: PropTypes.string,
  notesHelperBelow: PropTypes.string,
  submitLabel: PropTypes.string,
  submitSize: PropTypes.string,
  textAreaRows: PropTypes.number,
};

export default B2BForm;
